import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { RootState } from '.'
import {
  uploadMenuScan,
  getMenuScanStatus,
  getMenuScanSummary,
  saveMenuFromScan,
  type MenuScanResult,
  type MenuScanSummaryRequest,
  type MenuScanSummaryResponse,
  type MenuSaveRequest,
  type MenuSaveResponse,
} from '../api/menuScanApi'

export interface IMenuScanSlice {
  upload: {
    uploading: boolean
    error: string | null
    scanId: string | null
  }
  status: {
    loading: boolean
    error: string | null
    result: MenuScanResult | null
  }
  summary: {
    loading: boolean
    error: string | null
    data: MenuScanSummaryResponse | null
  }
  save: {
    saving: boolean
    error: string | null
    response: MenuSaveResponse | null
  }
}

const initialState: IMenuScanSlice = {
  upload: {
    uploading: false,
    error: null,
    scanId: null,
  },
  status: {
    loading: false,
    error: null,
    result: null,
  },
  summary: {
    loading: false,
    error: null,
    data: null,
  },
  save: {
    saving: false,
    error: null,
    response: null,
  },
}

/**
 * Upload menu scan file and get initial scanId.
 */
export const uploadMenuScanThunk = createAsyncThunk<
  MenuScanResult,
  { file: File; restaurantId?: string },
  { rejectValue: string }
>('menuScan/upload', async ({ file, restaurantId }, { rejectWithValue }) => {
  try {
    const result = await uploadMenuScan(file, restaurantId)
    return result
  } catch (error: any) {
    return rejectWithValue(error?.message ?? 'Upload failed')
  }
})

/**
 * Get current status of menu scan by scanId.
 */
export const getMenuScanStatusThunk = createAsyncThunk<
  MenuScanResult,
  string,
  { rejectValue: string }
>('menuScan/getStatus', async (scanId, { rejectWithValue }) => {
  try {
    const result = await getMenuScanStatus(scanId)
    return result
  } catch (error: any) {
    return rejectWithValue(error?.message ?? 'Failed to get scan status')
  }
})

/**
 * Get AI-generated summary for scanned menu.
 */
export const getMenuScanSummaryThunk = createAsyncThunk<
  MenuScanSummaryResponse,
  MenuScanSummaryRequest,
  { rejectValue: string }
>('menuScan/getSummary', async (payload, { rejectWithValue }) => {
  try {
    const result = await getMenuScanSummary(payload)
    return result
  } catch (error: any) {
    return rejectWithValue(error?.message ?? 'Failed to get summary')
  }
})

/**
 * Save menu from scan with optional restaurant linkage.
 */
export const saveMenuFromScanThunk = createAsyncThunk<
  MenuSaveResponse,
  { scanId: string; request: MenuSaveRequest },
  { rejectValue: string }
>('menuScan/save', async ({ scanId, request }, { rejectWithValue }) => {
  try {
    const result = await saveMenuFromScan(scanId, request)
    return result
  } catch (error: any) {
    return rejectWithValue(error?.message ?? 'Failed to save menu')
  }
})

const menuScanSlice = createSlice({
  name: 'menuScan',
  initialState,
  reducers: {
    resetUpload: (state) => {
      state.upload = initialState.upload
    },
    resetStatus: (state) => {
      state.status = initialState.status
    },
    resetSummary: (state) => {
      state.summary = initialState.summary
    },
    resetSave: (state) => {
      state.save = initialState.save
    },
    resetAll: () => initialState,
  },
  extraReducers: (builder) => {
    // Upload
    builder
      .addCase(uploadMenuScanThunk.pending, (state) => {
        state.upload.uploading = true
        state.upload.error = null
        state.upload.scanId = null
      })
      .addCase(uploadMenuScanThunk.fulfilled, (state, action) => {
        state.upload.uploading = false
        state.upload.scanId = action.payload.scanId
        state.upload.error = null
      })
      .addCase(uploadMenuScanThunk.rejected, (state, action) => {
        state.upload.uploading = false
        state.upload.error = action.payload ?? 'Upload failed'
      })

    // Status
    builder
      .addCase(getMenuScanStatusThunk.pending, (state) => {
        state.status.loading = true
        state.status.error = null
      })
      .addCase(getMenuScanStatusThunk.fulfilled, (state, action) => {
        state.status.loading = false
        state.status.result = action.payload
        state.status.error = null
      })
      .addCase(getMenuScanStatusThunk.rejected, (state, action) => {
        state.status.loading = false
        state.status.error = action.payload ?? 'Failed to get status'
      })

    // Summary
    builder
      .addCase(getMenuScanSummaryThunk.pending, (state) => {
        state.summary.loading = true
        state.summary.error = null
      })
      .addCase(getMenuScanSummaryThunk.fulfilled, (state, action) => {
        state.summary.loading = false
        state.summary.data = action.payload
        state.summary.error = null
      })
      .addCase(getMenuScanSummaryThunk.rejected, (state, action) => {
        state.summary.loading = false
        state.summary.error = action.payload ?? 'Failed to get summary'
      })

    // Save
    builder
      .addCase(saveMenuFromScanThunk.pending, (state) => {
        state.save.saving = true
        state.save.error = null
      })
      .addCase(saveMenuFromScanThunk.fulfilled, (state, action) => {
        state.save.saving = false
        state.save.response = action.payload
        state.save.error = null
      })
      .addCase(saveMenuFromScanThunk.rejected, (state, action) => {
        state.save.saving = false
        state.save.error = action.payload ?? 'Failed to save menu'
      })
  },
})

export const { resetUpload, resetStatus, resetSummary, resetSave, resetAll } =
  menuScanSlice.actions

// Selectors
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectMenuScanUpload = (state: RootState) => state.menuScan.upload
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectMenuScanStatus = (state: RootState) => state.menuScan.status
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectMenuScanSummary = (state: RootState) => state.menuScan.summary
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectMenuScanSave = (state: RootState) => state.menuScan.save

export default menuScanSlice.reducer
